import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Trash2, Calendar, ShieldAlert, Loader2 } from 'lucide-react';
import { API, useAuth } from '../App';

// Human-friendly names for each collection surfaced in the preview table
const COLLECTION_LABELS = {
  orders: 'Orders / payments',
  photo_sessions: 'Photo sessions',
  print_logs: 'Print logs',
  diagnostic_logs: 'Diagnostic logs',
  booth_commands: 'Booth commands',
  payment_anomalies: 'Payment anomalies',
  processed_webhooks: 'Processed webhooks',
  refunds: 'Refunds',
  crash_reports: 'Crash reports',
  voucher_redemptions: 'Voucher redemptions',
};

const isoNowMinusHours = (h) => {
  const d = new Date(Date.now() - h * 3600 * 1000);
  // datetime-local expects YYYY-MM-DDTHH:MM (no tz)
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const localToUtcIso = (local) => {
  if (!local) return '';
  const d = new Date(local);
  if (isNaN(d.getTime())) return '';
  return d.toISOString();
};

const TestDataCleanup = () => {
  const { token, user } = useAuth();
  const [beforeLocal, setBeforeLocal] = useState(isoNowMinusHours(0));
  const [kiosks, setKiosks] = useState([]);
  const [selectedKiosks, setSelectedKiosks] = useState([]);   // [] = all
  const [preview, setPreview] = useState(null);
  const [running, setRunning] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [emailConfirm, setEmailConfirm] = useState('');
  const [deleteResult, setDeleteResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/kiosks`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setKiosks(r.data || []))
      .catch(() => setKiosks([]));
  }, [token]);

  const runDryRun = async () => {
    const beforeIso = localToUtcIso(beforeLocal);
    if (!beforeIso) {
      toast.error('Pick a valid "delete before" date/time');
      return;
    }
    setRunning(true);
    setPreview(null);
    setDeleteResult(null);
    try {
      const body = {
        before_iso: beforeIso,
        dry_run: true,
      };
      if (selectedKiosks.length > 0) body.kiosk_ids = selectedKiosks;
      const res = await axios.post(`${API}/admin/test-data/cleanup`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPreview(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
    }
    setRunning(false);
  };

  const runDelete = async () => {
    if (!preview) {
      toast.error('Run a preview first');
      return;
    }
    if (confirmText !== 'DELETE TEST DATA') {
      toast.error('Type exactly: DELETE TEST DATA');
      return;
    }
    if (!emailConfirm || emailConfirm.toLowerCase().trim() !== (user?.email || '').toLowerCase().trim()) {
      toast.error('Email must exactly match your super_admin email');
      return;
    }
    // Extra safety browser confirm
    const total = preview.matched?._total || 0;
    if (!window.confirm(`Delete ${total} rows across ${Object.keys(preview.matched || {}).length - 1} collections?\nThis cannot be undone.`)) return;

    setRunning(true);
    try {
      const body = {
        before_iso: preview.before_iso,
        kiosk_ids: preview.kiosk_ids,
        dry_run: false,
        confirmation_text: confirmText,
        email_confirmation: emailConfirm,
      };
      const res = await axios.post(`${API}/admin/test-data/cleanup`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDeleteResult(res.data);
      setPreview(null);
      setConfirmText('');
      setEmailConfirm('');
      toast.success(`Deleted ${res.data.deleted?._total || 0} rows`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
    setRunning(false);
  };

  const toggleKiosk = (id) => {
    setSelectedKiosks(prev => prev.includes(id) ? prev.filter(k => k !== id) : [...prev, id]);
    setPreview(null);
  };

  const isSuper = user?.role === 'super_admin';
  if (!isSuper) {
    return (
      <div className="card-dashboard text-center py-12">
        <ShieldAlert className="w-10 h-10 text-red-500 mx-auto mb-3" />
        <p className="text-charcoal-800/70">Only super admins can access test data cleanup.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="test-data-cleanup-page">
      <div>
        <h1 className="font-serif text-3xl text-charcoal-900 flex items-center gap-2">
          <Trash2 className="w-6 h-6 text-red-600" /> Test data cleanup
        </h1>
        <p className="text-sm text-charcoal-800/60 mt-1 max-w-2xl">
          Surgically remove transactional data (orders, sessions, logs) created before a chosen date, optionally scoped to specific booths.
          Configuration data (booths, pricing, templates, vouchers, admins) is <span className="font-medium">never touched</span>.
          Analytics automatically recompute from the remaining rows.
        </p>
      </div>

      <div className="card-dashboard bg-amber-50 border border-amber-200 p-4 rounded-xl flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900">
          <div className="font-medium">Recommended workflow</div>
          <ol className="list-decimal ml-4 mt-1 space-y-0.5 text-amber-900/80">
            <li>Pick a cutoff date/time (typically the moment before your real launch)</li>
            <li>Preview the counts — verify the numbers look right</li>
            <li>Type <code className="bg-white/60 px-1 rounded">DELETE TEST DATA</code> + your admin email to confirm</li>
          </ol>
        </div>
      </div>

      {/* Filters */}
      <div className="card-dashboard space-y-4">
        <div>
          <label className="text-xs uppercase tracking-wider text-charcoal-800/60 flex items-center gap-1.5 mb-1.5">
            <Calendar className="w-3.5 h-3.5" /> Delete rows created before
          </label>
          <input
            type="datetime-local"
            value={beforeLocal}
            onChange={(e) => { setBeforeLocal(e.target.value); setPreview(null); }}
            className="border border-charcoal-900/10 rounded-lg px-3 py-2 text-sm w-full max-w-xs"
            data-testid="cleanup-before-input"
          />
          <div className="flex gap-2 mt-2 flex-wrap">
            {[
              { l: 'Now', h: 0 },
              { l: '1h ago', h: 1 },
              { l: '24h ago', h: 24 },
              { l: '7d ago', h: 24 * 7 },
            ].map(p => (
              <button
                key={p.l}
                onClick={() => { setBeforeLocal(isoNowMinusHours(p.h)); setPreview(null); }}
                className="text-xs px-2.5 py-1 border border-charcoal-900/10 rounded hover:bg-cream-50"
                data-testid={`cleanup-preset-${p.h}`}
              >
                {p.l}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-charcoal-800/60 mb-1.5 block">
            Scope by booths ({selectedKiosks.length === 0 ? 'ALL booths' : `${selectedKiosks.length} selected`})
          </label>
          <div className="border border-charcoal-900/10 rounded-lg divide-y divide-charcoal-900/5 max-h-56 overflow-y-auto">
            {kiosks.length === 0 && (
              <div className="text-xs text-charcoal-800/40 px-3 py-2">No booths</div>
            )}
            {kiosks.map(k => (
              <label key={k.id} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-cream-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedKiosks.includes(k.id)}
                  onChange={() => toggleKiosk(k.id)}
                  data-testid={`cleanup-kiosk-toggle-${k.id}`}
                />
                <span className="font-medium">{k.name}</span>
                <span className="text-xs text-charcoal-800/50">{k.venue_name || k.location || '—'}</span>
              </label>
            ))}
          </div>
        </div>

        <button
          onClick={runDryRun}
          disabled={running}
          className="btn-secondary inline-flex items-center gap-2"
          data-testid="cleanup-preview-btn"
        >
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {running ? 'Counting…' : 'Preview counts'}
        </button>
      </div>

      {/* Preview result */}
      {preview && (
        <div className="card-dashboard" data-testid="cleanup-preview-result">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-serif text-lg text-charcoal-900">Rows that would be deleted</h2>
            <span className="text-sm text-charcoal-800/60">
              Cutoff: <span className="font-mono">{preview.before_iso}</span>
            </span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-charcoal-800/60">
                <th className="py-1.5">Collection</th>
                <th className="py-1.5 text-right">Rows</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-charcoal-900/5">
              {Object.entries(preview.matched || {}).filter(([k]) => !k.startsWith('_')).map(([k, v]) => (
                <tr key={k}>
                  <td className="py-1.5">{COLLECTION_LABELS[k] || k}</td>
                  <td className="py-1.5 text-right font-mono">{v}</td>
                </tr>
              ))}
              <tr className="font-medium">
                <td className="py-2 pt-3 border-t border-charcoal-900/10">Total</td>
                <td className="py-2 pt-3 border-t border-charcoal-900/10 text-right font-mono" data-testid="cleanup-preview-total">
                  {preview.matched?._total || 0}
                </td>
              </tr>
            </tbody>
          </table>

          {(preview.matched?._total || 0) > 0 && (
            <div className="mt-6 pt-4 border-t border-charcoal-900/10 space-y-3">
              <p className="text-sm text-red-700 font-medium">Confirm deletion — this is irreversible.</p>
              <div>
                <label className="text-xs uppercase tracking-wider text-charcoal-800/60 mb-1 block">
                  Type <code className="bg-cream-100 px-1 rounded">DELETE TEST DATA</code>
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  className="border border-charcoal-900/10 rounded-lg px-3 py-2 text-sm w-full max-w-md"
                  data-testid="cleanup-confirm-phrase"
                  placeholder="DELETE TEST DATA"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-charcoal-800/60 mb-1 block">
                  Type your admin email ({user?.email})
                </label>
                <input
                  type="email"
                  value={emailConfirm}
                  onChange={(e) => setEmailConfirm(e.target.value)}
                  className="border border-charcoal-900/10 rounded-lg px-3 py-2 text-sm w-full max-w-md"
                  data-testid="cleanup-confirm-email"
                  placeholder={user?.email}
                />
              </div>
              <button
                onClick={runDelete}
                disabled={running || confirmText !== 'DELETE TEST DATA' || !emailConfirm}
                className="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-40 hover:bg-red-700"
                data-testid="cleanup-run-btn"
              >
                {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Delete {preview.matched?._total || 0} rows
              </button>
            </div>
          )}
        </div>
      )}

      {/* Post-delete result */}
      {deleteResult && (
        <div className="card-dashboard border-l-4 border-green-500" data-testid="cleanup-delete-result">
          <h2 className="font-serif text-lg text-charcoal-900 mb-2">Delete complete</h2>
          <p className="text-sm text-charcoal-800/60 mb-3">
            Removed <span className="font-mono font-medium">{deleteResult.deleted?._total || 0}</span> rows.
            Analytics dashboards will reflect the new totals on next refresh.
          </p>
          <details>
            <summary className="text-xs text-charcoal-800/50 cursor-pointer">Show breakdown</summary>
            <pre className="text-[10px] bg-charcoal-900/5 rounded p-2 mt-2 overflow-auto">
{JSON.stringify(deleteResult.deleted, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
};

export default TestDataCleanup;
